import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Divider, Stack } from "@mui/material";
import { useState } from "react";
import api, { DiscontinueProductsResultDto, ImportProductsResultDto, VendorDto, VendorUpdateDto } from "../../../api";
import { TabPanel } from "../../controls";
import { createDownloadLink, DefaultMessageDialogOptions, MessageDialog, MessageDialogButton, MessageDialogButtons, MessageDialogIcon, MessageDialogOptions, notif } from "../../utils";
import { ProductsTabPage } from "./ProductsTabPage";
import { VendorDialog } from "./VendorDialog";
import { VendorLogsGrid } from "./VendorLogsGrid";
import { VendorProductsDialog, VendorProductsDialogAction } from "./VendorProductsDialog";
import { VendorsGrid } from "./VendorsGrid";

type MessageOrigin = "delete" | "download";

export const VendorsPage = () => {
  const [selected, setSelected] = useState<VendorDto | null>(null);
  const [edit, setEdit] = useState<Partial<VendorUpdateDto> | null>(null);
  const [productsAction, setProductsAction] = useState<VendorProductsDialogAction>("none");
  const [message, setMessage] = useState<MessageDialogOptions<MessageOrigin>>(DefaultMessageDialogOptions<MessageOrigin>());
  const [reloadProducts, setReloadProducts] = useState(0);
  const [downloadIsLoading, setDownloadIsLoading] = useState(false);

  const onRowSelected = (row: VendorDto | null) => {
    setSelected(row);
  };
  const onInsert = () => {
    setEdit({ name: "", isEnabled: true });
  };
  const onEdit = () => {
    if (selected !== null) {
      setEdit({
        id: selected.id,
        name: selected.name,
        supplierId: selected.supplierId,
        isEnabled: selected.isEnabled,
      });
    }
  };
  const onDelete = () => {
    if (selected?.id) {
      setMessage({
        open: true,
        title: "DELETE VENDOR",
        buttons: MessageDialogButtons.YesNo,
        icon: MessageDialogIcon.Question,
        content: `Do you want to delete vendor '${selected.name}'?`,
        checks: [{ label: "Delete all dependent entities", value: false }],
        origin: "delete",
      });
    }
  };
  const deleteVendor = async (cascade?: boolean) => {
    if (selected?.id) {
      try {
        await api.vendor.delete(cascade ?? false, selected.id);
      } catch (err) {
        notif(err, "error");
      }
    }
  };

  const onDownloadClick = async () => {
    if (selected?.id) {
      setMessage({
        open: true,
        title: "DOWNLOAD INVENTORY",
        buttons: MessageDialogButtons.YesNo,
        icon: MessageDialogIcon.Question,
        content: `Do you want to download vendor '${selected.name}' inventory?`,
        origin: "download",
      });
    }
  };

  const download = async () => {
    if (selected?.id) {
      setDownloadIsLoading(true);
      try {
        const file = await api.vendor.download(selected.id);
        createDownloadLink(file, window);
      } catch (err) {
        notif(err, "error");
      }
      setDownloadIsLoading(false);
    }
  };

  const onMessageSubmit = async (button: MessageDialogButton, origin?: MessageOrigin, checks?: boolean[]) => {
    setMessage({ open: false });
    if (origin === "delete" && button === MessageDialogButton.Yes) {
      await deleteVendor(checks ? checks[0] : false);
    } else if (origin === "download" && button === MessageDialogButton.Yes) {
      await download();
    }
  };
  const onVendorDialogSubmit = (dto: VendorDto | null) => {
    setEdit(null);
    if (dto !== null) setSelected(dto);
  };
  const onVendorProductsDialogClose = () => {
    setProductsAction("none");
  };

  const onImportProducts = () => {
    if (selected !== null) setProductsAction("import");
  };
  const onImportProductsDone = (result: ImportProductsResultDto) => {
    setProductsAction("none");
    setReloadProducts(reloadProducts + 1);
    notif(`Import completed: inserted ${result?.inserted} products, updated ${result?.updated} products`);
  };

  const onDiscontinueProducts = () => {
    if (selected !== null) setProductsAction("discontinue");
  };
  const onDiscontinueProductsDone = (result: DiscontinueProductsResultDto) => {
    setProductsAction("none");
    setReloadProducts(reloadProducts + 1);
    notif(`Discontinue products completed: updated ${result?.updated} products`);
  };

  const tabLogs = () => {
    return (
      <Box>
        <VendorLogsGrid vendorId={selected?.id || ""} />
      </Box>
    );
  };

  const tabVendors = () => (
    <Box maxWidth="900px">
      <VendorsGrid onRowSelected={onRowSelected} />
      <Box sx={{ p: 1 }} />
      <Stack spacing={2} direction="row">
        <Button variant="contained" onClick={onInsert}>
          Add
        </Button>
        <Button variant="contained" disabled={!selected?.id} onClick={onEdit}>
          Edit
        </Button>
        <Button variant="contained" disabled={!selected?.id} onClick={onDelete}>
          Delete
        </Button>
        <Button variant="contained" disabled={!selected?.id} onClick={onImportProducts}>
          Import
        </Button>
        <Button variant="contained" disabled={!selected?.id} onClick={onDiscontinueProducts}>
          Discontinue
        </Button>
        <LoadingButton variant="contained" disabled={!selected?.id} endIcon={<SaveAltOutlinedIcon />} loadingPosition="end" loading={downloadIsLoading} onClick={download}>
          Download
        </LoadingButton>
      </Stack>
      <VendorDialog vendor={edit} onSubmit={onVendorDialogSubmit} />
      <VendorProductsDialog action={productsAction} vendor={selected} onClose={onVendorProductsDialogClose} onImport={onImportProductsDone} onDiscontinue={onDiscontinueProductsDone} />
      <MessageDialog options={message} onSubmit={onMessageSubmit} />
      <Box sx={{ marginTop: 2 }}>
        <Divider variant="middle" />
      </Box>
      <TabPanel
        name="details"
        items={[
          { label: "PRODUCTS", children: <ProductsTabPage vendor={selected}/> },
          { label: "LOGS", children: tabLogs() },
        ]}
      />
    </Box>
  );

  return (
    <Box>
      <TabPanel name="vendors" items={[{ label: "VENDORS", children: tabVendors() }]} />
    </Box>
  );
};
